import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useSelector } from 'react-redux';
import Image from '../../Utils/Image/image-component';

/**
 * High-impact Google review card: full-color Google branding and
 * portrait-safe (no-crop) image handling.
 */
const GOOGLE_LOGO = 'https://sawpy-assets.s3.us-east-2.amazonaws.com/logos/google.png';
const TRUNCATE_AT = 180;

const ReviewCard = ({ name, reviewText, shortText, image, reviewDate }) => {
    const dir = useSelector((state) => state.systemData?.dir) || 'ltr';
    const [open, setOpen] = useState(false);

    // Authentic Google Avatar Palette
    const avatarColors = ['bg-[#1a73e8]', 'bg-[#d93025]', 'bg-[#f9ab00]', 'bg-[#188038]', 'bg-[#af5cf7]'];
    const colorIndex = name ? name.length % avatarColors.length : 0;

    const isTruncated = reviewText?.length > TRUNCATE_AT;
    const displayText = isTruncated ? reviewText.slice(0, TRUNCATE_AT).trimEnd() : reviewText;

    return (
        <>
        <div dir={dir} className='flex py-4 h-full'>
            <div className={`bg-white font-primary p-5 w-[300px] lg:w-[340px] rounded-3xl shadow-md border border-slate-100 dark:bg-zinc-900 dark:border-zinc-800 flex flex-col group transition-all duration-300 hover:shadow-2xl ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                
                <article className="flex flex-col h-full">
                    
                    {/* --- HEADER: USER INFO & LARGE LOGO --- */}
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                            {/* Google profile photo, initial fallback */}
                            {image ? (
                                <div className="w-11 h-11 rounded-full overflow-hidden shrink-0 shadow-sm bg-gray-100 dark:bg-zinc-800">
                                    <Image
                                        src={image}
                                        alt={name}
                                        className="w-full h-full object-cover"
                                        useSekeleton={true}
                                        quality={80}
                                    />
                                </div>
                            ) : (
                                <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white text-lg font-bold ${avatarColors[colorIndex]} shrink-0 shadow-sm`}>
                                    {name ? name.charAt(0).toUpperCase() : 'G'}
                                </div>
                            )}

                            <div className="flex flex-col">
                                <h3 className="text-base font-bold text-gray-900 dark:text-white leading-tight">
                                    {name}
                                </h3>
                                <div className="flex items-center gap-2 mt-1">
                                    <div className="flex gap-0.5">
                                        {[...Array(5)].map((_, i) => (
                                            <svg key={i} className="w-4 h-4 text-[#fbbc04]" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                            </svg>
                                        ))}
                                    </div>
                                    {reviewDate && (
                                        <span className="text-xs text-gray-400 font-medium">{reviewDate}</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Large Full-Color Google Logo */}
                        <div className="flex justify-center items-center p-2 rounded-2xl">
                            <img
                                alt="Google"
                                src={GOOGLE_LOGO}
                                loading="lazy"
                                className="w-7 h-7 object-contain"
                            />
                        </div>
                    </div>

                    {/* --- REVIEW TEXT --- */}
                    <div className="flex-grow space-y-2">
                        {shortText && (
                            <h4 className="font-secondary text-lg font-bold leading-snug text-primary-500 dark:text-white">
                                {shortText}
                            </h4>
                        )}
                        <p className="text-sm leading-relaxed text-gray-700 dark:text-zinc-300 font-medium italic">
                            "{displayText}
                            {isTruncated && (
                                <>
                                    {'... '}
                                    <button
                                        onClick={() => setOpen(true)}
                                        className="not-italic text-[#1a73e8] font-bold hover:underline"
                                    >
                                        Read more
                                    </button>
                                </>
                            )}
                            {!isTruncated && '"'}
                        </p>
                    </div>

                </article>
            </div>
        </div>

            {/* Full-review modal, portalled so fixed positioning works on mobile */}
            {open && createPortal(
                <div
                    dir={dir}
                    className="fixed inset-0 z-[9999] font-primary flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                    onClick={() => setOpen(false)}
                >
                    <div
                        className="relative bg-white dark:bg-zinc-900 rounded-3xl shadow-2xl max-w-lg w-full p-8 flex flex-col gap-4 max-h-[85vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setOpen(false)}
                            className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-700 dark:hover:text-white transition-colors"
                            aria-label="Close"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        <div className="flex items-center gap-3">
                            {image ? (
                                <div className="w-11 h-11 rounded-full overflow-hidden shrink-0 shadow-sm bg-gray-100 dark:bg-zinc-800">
                                    <Image src={image} alt={name} className="w-full h-full object-cover" useSekeleton={true} quality={80} />
                                </div>
                            ) : (
                                <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white text-lg font-bold ${avatarColors[colorIndex]} shrink-0 shadow-sm`}>
                                    {name ? name.charAt(0).toUpperCase() : 'G'}
                                </div>
                            )}
                            <div className="flex flex-col">
                                <h3 className="text-base font-bold text-gray-900 dark:text-white leading-tight">{name}</h3>
                                <div className="flex gap-0.5 mt-1">
                                    {[...Array(5)].map((_, i) => (
                                        <svg key={i} className="w-4 h-4 text-[#fbbc04]" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {shortText && (
                            <h4 className="font-secondary text-lg font-bold leading-snug text-primary-500 dark:text-white">
                                {shortText}
                            </h4>
                        )}
                        <p className="text-sm leading-relaxed text-gray-700 dark:text-zinc-300 font-medium italic">
                            "{reviewText}"
                        </p>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default ReviewCard;